import React, { useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  FormGroup,
  Input,
  Label,
  Table,
} from 'reactstrap';
import { useKindeAuth } from '@kinde-oss/kinde-auth-react';
import { deleteData, fetchDataAgnostic, postDataAgnostic } from 'Services/dataApi';
import { formatBearerToken } from 'Common.functions';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';

const cardStyle = {
  width: '100%',
  maxWidth: 1400,
  marginTop: 12,
};

const statusColorMap = {
  created: 'secondary',
  confirmed: 'secondary',
  sent_to_author: 'info',
  author_opened: 'info',
  in_progress: 'warning',
  uploaded: 'primary',
  submitted: 'success',
  supplement_required: 'warning',
  assigned_to_program: 'success',
  released_to_program: 'success',
  viewed_by_program: 'dark',
};
const hiddenApplicantStatuses = new Set(['assigned_to_program']);

const MAX_RECOMMENDER_REQUESTS = 5;
const defaultRequestEntry = {
  AuthorName: '',
  AuthorEmail: '',
  AuthorInstitution: '',
  AuthorTitle: '',
};
const buildDefaultRequestForm = () => ({
  Requests: Array.from({ length: MAX_RECOMMENDER_REQUESTS }, () => ({ ...defaultRequestEntry })),
  WaiverAcknowledged: false,
});

const normalizeEmail = (value) => `${value || ''}`.trim().toLowerCase();
const REMINDER_COOLDOWN_MS = 30 * 1000;

const ApplicantLettersPage = ({ applicantDetails: _applicantDetails }) => {
  const { getToken, isAuthenticated } = useKindeAuth();
  const [selectedApplicationRecord, setSelectedApplicationRecord] = useState(null);
  const [letters, setLetters] = useState([]);
  const [requestForm, setRequestForm] = useState(() => buildDefaultRequestForm());
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [reminderCooldownUntilByRequest, setReminderCooldownUntilByRequest] = useState({});
  const [currentTimestampMs, setCurrentTimestampMs] = useState(Date.now());

  const requestEntryValidation = useMemo(() => {
    const existingRequestEmails = new Set(letters.map((letter) => normalizeEmail(letter.AuthorEmail)).filter((email) => !!email));
    const normalizedEntries = requestForm.Requests.map((entry) => {
      const normalizedEntry = {
        AuthorName: `${entry?.AuthorName || ''}`.trim(),
        AuthorEmail: `${entry?.AuthorEmail || ''}`.trim(),
        AuthorInstitution: `${entry?.AuthorInstitution || ''}`.trim(),
        AuthorTitle: `${entry?.AuthorTitle || ''}`.trim(),
      };
      const hasAnyValue = !!(
        normalizedEntry.AuthorName ||
        normalizedEntry.AuthorEmail ||
        normalizedEntry.AuthorInstitution ||
        normalizedEntry.AuthorTitle
      );
      return {
        ...normalizedEntry,
        hasAnyValue,
        normalizedEmail: normalizeEmail(normalizedEntry.AuthorEmail),
      };
    });

    const emailCounts = normalizedEntries.reduce((accumulator, entry) => {
      if (!entry.hasAnyValue || !entry.normalizedEmail) {
        return accumulator;
      }
      return {
        ...accumulator,
        [entry.normalizedEmail]: (accumulator[entry.normalizedEmail] || 0) + 1,
      };
    }, {});

    return normalizedEntries.map((entry) => {
      const errors = [];
      if (entry.hasAnyValue) {
        if (!entry.normalizedEmail) {
          errors.push('Author email is required for completed entries.');
        } else if (!entry.normalizedEmail.includes('@')) {
          errors.push('Enter a valid email address.');
        }

        if (entry.normalizedEmail && existingRequestEmails.has(entry.normalizedEmail)) {
          errors.push('A request has already been sent to this email.');
        }

        if (entry.normalizedEmail && (emailCounts[entry.normalizedEmail] || 0) > 1) {
          errors.push('This email is duplicated in this submission.');
        }
      }

      return {
        ...entry,
        errors,
      };
    });
  }, [letters, requestForm.Requests]);
  const completedRequestEntries = useMemo(
    () => requestEntryValidation.filter((entry) => entry.hasAnyValue),
    [requestEntryValidation],
  );
  const hasInvalidCompletedEntries = useMemo(
    () => requestEntryValidation.some((entry) => entry.hasAnyValue && entry.errors.length > 0),
    [requestEntryValidation],
  );
  const isSendRequestDisabled =
    isSaving ||
    isLoading ||
    !selectedApplicationRecord ||
    !requestForm.WaiverAcknowledged ||
    completedRequestEntries.length === 0 ||
    hasInvalidCompletedEntries;

  const getAuthHeaders = async () => {
    const token = await getToken();
    return formatBearerToken(token);
  };

  const fetchApplications = async () => {
    if (!isAuthenticated) {
      return;
    }

    setIsLoading(true);
    const headers = await getAuthHeaders();
    return fetchDataAgnostic('applicant/application/list', null, headers)
      .then((response) => {
        const receivedApplications = response.data || [];

        if (receivedApplications.length > 0 && !selectedApplicationRecord) {
          setSelectedApplicationRecord(receivedApplications[0].pk_ApplicationRecord);
        }
      })
      .catch((error) => {
        toast.error('Failed to load applications');
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const fetchLetters = async () => {
    setIsLoading(true);
    const headers = await getAuthHeaders();
    return fetchDataAgnostic('applicant/letters/list', null, headers)
      .then((lettersResponse) => {
        const { requests = [] } = lettersResponse.data || {};
        setLetters(requests);
      })
      .catch((error) => {
        toast.error('Failed to load letter details');
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    fetchApplications();
    fetchLetters();
  }, [isAuthenticated]);

  useEffect(() => {
    const timerId = setInterval(() => {
      setCurrentTimestampMs(Date.now());
    }, 1000);

    return () => {
      clearInterval(timerId);
    };
  }, []);

  const updateRequestEntryField = (entryIndex, fieldName, value) => {
    setRequestForm((prev) => ({
      ...prev,
      Requests: prev.Requests.map((entry, currentIndex) =>
        currentIndex === entryIndex ? { ...entry, [fieldName]: value } : entry,
      ),
    }));
  };

  const submitLetterRequest = async () => {
    if (completedRequestEntries.length === 0) {
      toast.error('Complete at least one recommender section before sending requests');
      return;
    }

    const firstValidationError = requestEntryValidation
      .map((entry, index) => ({ entry, index }))
      .find(({ entry }) => entry.hasAnyValue && entry.errors.length > 0);
    if (firstValidationError) {
      toast.error(`Recommender ${firstValidationError.index + 1}: ${firstValidationError.entry.errors[0]}`);
      return;
    }

    if (!requestForm.WaiverAcknowledged) {
      toast.error('Waiver acknowledgment is required before sending request');
      return;
    }

    if (!selectedApplicationRecord) {
      toast.error('No active application found for this applicant');
      return;
    }

    setIsSaving(true);
    const headers = await getAuthHeaders();
    return postDataAgnostic(
      'applicant/letters/request',
      null,
      {
        Requests: completedRequestEntries.map((entry) => ({
          AuthorName: entry.AuthorName,
          AuthorEmail: entry.AuthorEmail,
          AuthorInstitution: entry.AuthorInstitution,
          AuthorTitle: entry.AuthorTitle,
        })),
        WaiverAcknowledged: requestForm.WaiverAcknowledged,
        pk_ApplicationRecord: selectedApplicationRecord,
      },
      headers,
    )
      .then((response) => {
        const submittedRequests = response?.data?.requests || (response?.data?.request ? [response.data.request] : []);
        if (submittedRequests.length > 1) {
          toast.success(`${submittedRequests.length} letter requests sent to authors`);
        } else {
          toast.success('Letter request sent to author');
        }
        setRequestForm(buildDefaultRequestForm());
        fetchLetters();
      })
      .catch((error) => {
        toast.error(error?.response?.data?.message || 'Failed to create request');
      })
      .finally(() => {
        setIsSaving(false);
      });
  };

  const resolveSentToAuthorEvents = (letter) => {
    return (letter?.StatusEvents || []).filter((event) => `${event?.Status || ''}`.toLowerCase() === 'sent_to_author');
  };

  const resolveRequestedOnTimestamp = (letter) => {
    const sentToAuthorEvents = resolveSentToAuthorEvents(letter);
    if (sentToAuthorEvents.length > 0) {
      return sentToAuthorEvents[sentToAuthorEvents.length - 1].CreatedAt;
    }
    return letter?.createdAt || null;
  };

  const resolveReminderSentTimestamp = (letter) => {
    const sentToAuthorEvents = resolveSentToAuthorEvents(letter);
    if (sentToAuthorEvents.length > 0) {
      return sentToAuthorEvents[0].CreatedAt;
    }
    return resolveRequestedOnTimestamp(letter);
  };

  const resolveReminderCooldownUntil = (letter) => {
    const reminderTimestamp = resolveReminderSentTimestamp(letter);
    const eventCooldownUntil = reminderTimestamp ? dayjs(reminderTimestamp).valueOf() + REMINDER_COOLDOWN_MS : 0;
    const localCooldownUntil = reminderCooldownUntilByRequest[letter.pk_LoRRequest] || 0;
    return Math.max(eventCooldownUntil, localCooldownUntil);
  };

  const sendReminder = async (letter) => {
    const cooldownUntil = resolveReminderCooldownUntil(letter);
    if (cooldownUntil > Date.now()) {
      const secondsRemaining = Math.max(1, Math.ceil((cooldownUntil - Date.now()) / 1000));
      toast.error(`Please wait ${secondsRemaining} seconds before sending another reminder`);
      return;
    }

    const headers = await getAuthHeaders();
    return postDataAgnostic('applicant/letters/remind', { pk_LoRRequest: letter.pk_LoRRequest }, null, headers)
      .then(() => {
        toast.success('Reminder sent');
        setReminderCooldownUntilByRequest((prev) => ({
          ...prev,
          [letter.pk_LoRRequest]: Date.now() + REMINDER_COOLDOWN_MS,
        }));
        fetchLetters();
      })
      .catch((error) => {
        toast.error(error?.response?.data?.message || 'Failed to send reminder');
      });
  };

  const deleteRequest = async (pkLoRRequest) => {
    const headers = await getAuthHeaders();
    return deleteData('applicant/letters/request', { pk_LoRRequest: pkLoRRequest }, null, headers)
      .then(() => {
        toast.success('Letter request removed');
        fetchLetters();
      })
      .catch((error) => {
        toast.error(error?.response?.data?.message || 'Failed to delete letter request');
      });
  };

  const renderLettersTable = () => {
    return (
      <Table responsive size="sm" bordered hover>
        <thead>
          <tr>
            <th>Author</th>
            <th>Email</th>
            <th>Type</th>
            <th>Status</th>
            <th>Requested On</th>
            <th>Reminder Sent</th>
            <th>Submitted</th>
            <th>Missing</th>
            <th>Shared Apps</th>
            <th style={{ width: 210 }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {letters.length === 0 ? (
            <tr>
              <td colSpan={10}>No letter requests yet.</td>
            </tr>
          ) : (
            letters.map((letter) => {
              const submittedAt = letter.SubmittedAt ? dayjs(letter.SubmittedAt).format('MMM D, YYYY h:mm A') : '--';
              const firstDisplayableStatusEvent = (letter.StatusEvents || []).find(
                (event) => !hiddenApplicantStatuses.has(`${event?.Status || ''}`.toLowerCase()),
              );
              const latestStatus =
                firstDisplayableStatusEvent?.Status || letter.LatestStatus || letter.Status || 'created';
              const assignedCount = (letter.AssignedApplicationRecordIds || []).length;
              const isSubmitted = latestStatus === 'submitted' || !!letter.SubmittedAt || !!letter.Locked;
              const missingCount = Number(letter.MissingComponentCount || 0);
              const requestedOn = resolveRequestedOnTimestamp(letter);
              const reminderSentOn = resolveReminderSentTimestamp(letter);
              const reminderCooldownUntil = resolveReminderCooldownUntil(letter);
              const isReminderCoolingDown = reminderCooldownUntil > currentTimestampMs;
              const reminderCooldownSeconds = Math.max(
                1,
                Math.ceil((reminderCooldownUntil - currentTimestampMs) / 1000),
              );

              return (
                <tr key={letter.pk_LoRRequest}>
                  <td>{letter.AuthorName || '--'}</td>
                  <td>{letter.AuthorEmail}</td>
                  <td>{letter.LetterType}</td>
                  <td>
                    <Badge color={statusColorMap[latestStatus] || 'secondary'}>{latestStatus}</Badge>
                  </td>
                  <td>{requestedOn ? dayjs(requestedOn).format('MMM D, YYYY h:mm A') : '--'}</td>
                  <td>{reminderSentOn ? dayjs(reminderSentOn).format('MMM D, YYYY h:mm A') : '--'}</td>
                  <td>{submittedAt}</td>
                  <td>
                    <Badge color={missingCount > 0 ? 'warning' : 'success'}>{missingCount}</Badge>
                  </td>
                  <td>{assignedCount}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      <Button
                        size="sm"
                        color="primary"
                        disabled={isSubmitted || isReminderCoolingDown}
                        onClick={() => sendReminder(letter)}
                      >
                        {isReminderCoolingDown ? `Remind (${reminderCooldownSeconds}s)` : 'Remind'}
                      </Button>
                      <Button
                        size="sm"
                        color="danger"
                        disabled={isSubmitted}
                        onClick={() => deleteRequest(letter.pk_LoRRequest)}
                      >
                        Delete
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </Table>
    );
  };

  return (
    <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
      <div style={{ width: '95%', maxWidth: 1500 }}>
        <Card style={cardStyle}>
          <CardHeader>
            <b>Letters of Recommendation</b>
          </CardHeader>
          <CardBody>
            <div style={{ fontSize: 12, color: '#666', marginBottom: 8 }}>
              Letter requests are shared across all active applications by default.
            </div>
            {renderLettersTable()}
          </CardBody>
        </Card>

        <Card style={cardStyle}>
          <CardHeader>
            <b>Request a New Letter</b>
          </CardHeader>
          <CardBody>
            <div style={{ fontSize: 12, color: '#666', marginBottom: 10 }}>
              You can send up to {MAX_RECOMMENDER_REQUESTS} recommendation requests at once. Leave unused sections
              blank.
            </div>
            <div style={{ fontSize: 12, color: '#666', marginBottom: 10 }}>
              {completedRequestEntries.length} of {MAX_RECOMMENDER_REQUESTS} recommender sections ready to send.
            </div>
            <div style={{ display: 'grid', gap: 10 }}>
              {requestEntryValidation.map((entry, entryIndex) => (
                <div
                  key={`request-entry-${entryIndex}`}
                  style={{
                    border: '1px solid #e3e5e8',
                    borderRadius: 8,
                    padding: 10,
                  }}
                >
                  <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 10 }}>Recommender {entryIndex + 1}</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: 10 }}>
                    <FormGroup>
                      <Label>Author Name</Label>
                      <Input
                        value={requestForm.Requests[entryIndex].AuthorName}
                        onChange={(event) => updateRequestEntryField(entryIndex, 'AuthorName', event.target.value)}
                      />
                    </FormGroup>
                    <FormGroup>
                      <Label>Author Email</Label>
                      <Input
                        value={requestForm.Requests[entryIndex].AuthorEmail}
                        onChange={(event) => updateRequestEntryField(entryIndex, 'AuthorEmail', event.target.value)}
                      />
                    </FormGroup>
                    <FormGroup>
                      <Label>Institution</Label>
                      <Input
                        value={requestForm.Requests[entryIndex].AuthorInstitution}
                        onChange={(event) =>
                          updateRequestEntryField(entryIndex, 'AuthorInstitution', event.target.value)
                        }
                      />
                    </FormGroup>
                    <FormGroup>
                      <Label>Job Title</Label>
                      <Input
                        value={requestForm.Requests[entryIndex].AuthorTitle}
                        onChange={(event) => updateRequestEntryField(entryIndex, 'AuthorTitle', event.target.value)}
                      />
                    </FormGroup>
                  </div>
                  {entry.hasAnyValue && entry.errors.length > 0 ? (
                    <div style={{ fontSize: 12, color: '#b33' }}>{entry.errors[0]}</div>
                  ) : null}
                </div>
              ))}
            </div>
            <FormGroup check style={{ marginBottom: 12 }}>
              <Label check>
                <Input
                  type="checkbox"
                  checked={!!requestForm.WaiverAcknowledged}
                  onChange={(event) =>
                    setRequestForm((prev) => ({
                      ...prev,
                      WaiverAcknowledged: event.target.checked,
                    }))
                  }
                />{' '}
                I acknowledge and waive my right to view Letters of Recommendation in RezRATE. I will only be able to
                track LoR status, and submitted letters may be shared with programs I apply to.
              </Label>
            </FormGroup>
            <Button color="primary" disabled={isSendRequestDisabled} onClick={submitLetterRequest}>
              Send Request
            </Button>
          </CardBody>
        </Card>
      </div>
    </div>
  );
};

export default ApplicantLettersPage;
